import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgeArticles } from '@/mocks/homeData';
import { useScrollReveal, useScrollRevealMultiple } from '@/hooks/useScrollReveal';

export default function KnowledgeSection() {
  const navigate = useNavigate();
  const [activeTag, setActiveTag] = useState('聽力檢測');
  const { ref: topRef, isVisible: topVisible } = useScrollReveal();
  const { setRef, visible } = useScrollRevealMultiple(knowledgeArticles.length + 1);

  const tags = ['聽力檢測', '助聽器保養', '噪音防護'];

  return (
    <section id="knowledge-section" className="py-8 md:py-12 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        {/* 顶部：左右两栏重新排版 */}
        <div
          ref={topRef}
          className={`flex flex-col md:flex-row gap-8 md:gap-12 scroll-reveal ${topVisible ? 'scroll-reveal-visible' : ''}`}
        >
          {/* 左侧：大图区域 */}
          <div className="w-full md:w-[55%] lg:w-[50%]">
            <div className="rounded-2xl overflow-hidden h-[320px] md:h-[480px] lg:h-[520px] relative shadow-md">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20audiologist%20conducting%20hearing%20test%20for%20elderly%20patient%20in%20modern%20clinic%20warm%20soft%20lighting%20professional%20atmosphere%20high%20quality&width=600&height=420&seq=40&orientation=landscape"
                alt="專業聽力檢測"
                className="w-full h-full object-cover object-top"
              />
              {/* 圖片左下角文字浮層 */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 md:p-8">
                <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                  聽力健康
                </span>
                <h3 className="text-white text-xl md:text-2xl font-bold leading-tight">
                  專業聽力檢測服務
                </h3>
              </div>
            </div>
            {/* 图片下方标签 */}
            <div className="flex flex-wrap gap-3 mt-5">
              {['聽力保健', '衛教新知', '專家建議'].map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 rounded-full bg-white text-base text-[#1B3D2A] font-medium border border-[#E8E0D6] hover:border-[#D4A574] transition-colors cursor-default"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* 右侧：内容区域 */}
          <div className="w-full md:w-[45%] lg:w-[50%] flex flex-col">
            {/* 标题区 */}
            <div className="mb-6 md:mb-8">
              <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                聽力知識
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1B3D2A] leading-tight">
                守護您的聽力健康
              </h2>
              <p className="mt-4 text-lg text-[#5A6B5A] leading-relaxed">
                由富聆聽專業聽力師團隊提供最新聽力保健知識，幫助您及早發現、及早預防聽力問題。
              </p>
            </div>

            {/* 标签按钮 */}
            <div className="flex flex-wrap gap-3">
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-4 md:px-6 py-3 md:py-3.5 min-h-[44px] rounded-full text-base font-medium transition-all whitespace-nowrap border flex items-center justify-center ${
                    activeTag === tag
                      ? 'bg-[#1B3D2A] text-white border-[#1B3D2A]'
                      : 'bg-white text-[#5A6B5A] border-[#E8E0D6] hover:border-[#D4A574] hover:text-[#1B3D2A]'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            {/* 深色卡片 - 耳鳴精選專題 */}
            <div className="mt-6 md:mt-8 flex-1 bg-[#1B3D2A] rounded-2xl overflow-hidden flex flex-col hover:shadow-lg transition-shadow duration-300 cursor-pointer"
              onClick={() => navigate('/article/7')}
            >
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <span className="inline-flex items-center gap-2 text-sm text-[#D4A574] font-medium border border-[#D4A574]/40 px-3 py-1.5 rounded-full w-fit">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4A574] inline-block"></span>
                  精選專題
                </span>
                <h3 className="mt-5 text-white text-xl font-semibold leading-snug">
                  耳鳴警訊：當耳朵開始嗡嗡響時該怎麼辦？
                </h3>
                <p className="mt-4 text-white/70 text-base leading-relaxed flex-1">
                  耳鳴不只是小困擾，可能是聽力問題的警訊。了解耳鳴的成因、影響與應對方式，別讓忽視造成遺憾。
                </p>
                <div className="mt-6 text-[#D4A574] text-base font-medium flex items-center gap-2 hover:text-white transition-colors w-fit group">
                  閱讀更多
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部：文章卡片 */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {knowledgeArticles.map((article, i) => (
            <div
              key={article.id}
              ref={setRef(i)}
              className={`bg-white rounded-xl overflow-hidden border border-[#E8E0D6] hover:border-[#D4A574]/50 cursor-pointer transition-all duration-300 group hover:shadow-md scroll-reveal scroll-reveal-delay-${Math.min(i + 1, 6)} ${visible[i] ? 'scroll-reveal-visible' : ''}`}
              onClick={() => navigate(`/article/${article.id}`)}
            >
              <div className="w-full h-44 overflow-hidden relative">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
                {/* 圖片底部漸層 */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              <div className="p-5 md:p-6">
                <span className="inline-block text-xs font-semibold text-[#1B3D2A] bg-[#E8E0D6]/50 px-3 py-1.5 rounded-full">
                  {article.category}
                </span>
                <h3 className="mt-3 text-base font-semibold text-[#1B3D2A] leading-snug group-hover:text-[#D4A574] transition-colors duration-300">
                  {article.title}
                </h3>
                <p className="mt-2 text-[#5A6B5A] text-base leading-relaxed line-clamp-2">
                  {article.summary}
                </p>
                <div className="mt-4 flex items-center gap-1 text-sm text-[#D4A574] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  閱讀全文
                  <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-0.5"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 查看更多文章 */}
        <div className="mt-10 md:mt-12 text-center">
          <button
            type="button"
            onClick={() => navigate('/knowledge')}
            className="inline-flex items-center gap-2.5 bg-[#1B3D2A] text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#143026] transition-all duration-300 whitespace-nowrap group"
          >
            <span>查看更多文章</span>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-white text-base group-hover:translate-x-0.5 transition-transform duration-300" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}